import ExcelJS from "exceljs";
import {
  CellValue,
  VerticalSeasons,
  HorizontalHeaders,
} from "../pages/components/FuturesTable/utils/header.utils";
import { AT_ONCE_COLUMNS } from "../contexts/AtOnceContext";
import { DISTRIBUTION_COLUMNS } from "../contexts/DistributionContext";
import { TOTAL_BUSINESS_MODEL_HEADERS } from "../pages/components/TotalBusinessModelTable/TotalBusinessModel.headers";

const CELL_VALUES = [
  "B",
  "C",
  "D",
  "E",
  "F",
  "G",
  "H",
  "I",
  "J",
  "K",
  "L",
  "M",
  "N",
  "O",
  "P",
  "Q",
  "R",
  "S",
  "T",
  "U",
  "V",
  "W",
  "X",
  "Y",
  "Z",
];

const TABLE_SEPARATION = 5;
const TABLES_START = 3;
const styleCell = (worksheetCell: ExcelJS.Cell, value: string) => {
  worksheetCell.value = value;
  worksheetCell.border = {
    top: { style: "thin" },
    left: { style: "thin" },
    bottom: { style: "thin" },
    right: { style: "thin" },
  };
  worksheetCell.fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: "D9D9D9" },
  };
  worksheetCell.alignment = { vertical: "middle", horizontal: "center" };
};
const mergeCells = (
  worksheet: ExcelJS.Worksheet,
  HEADERS: CellValue[],
  row: number,
  total: number,
) => {
  if (!HEADERS.length) {
    return;
  }
  const [head, ...tail] = HEADERS;
  // ignore cells with 1
  const cell = `${CELL_VALUES[total]}${row}`;
  if (head.colSpan > 1) {
    const value = `${cell}:${CELL_VALUES[total + head.colSpan - 1]}${row}`;
    worksheet.mergeCells(value);
  }
  const worksheetCell = worksheet.getCell(cell);
  styleCell(worksheetCell, head.value);
  // set accent style to match headers
  return mergeCells(worksheet, tail, row, total + head.colSpan);
};

const mergeVerticalByRow = (
  worksheet: ExcelJS.Worksheet,
  HEADERS: string[],
  row: number,
) => {
  if (!HEADERS.length) {
    return;
  }
  const [head, ...tail] = HEADERS;
  // ignore cells with 1
  const cell = `${CELL_VALUES[0]}${row}`;
  const value = `${cell}:${CELL_VALUES[1]}${row}`;
  worksheet.mergeCells(value);
  const worksheetCell = worksheet.getCell(cell);
  styleCell(worksheetCell, head);
  // set accent style to match headers
  return mergeVerticalByRow(worksheet, tail, row + 1);
};
const mergeHorizontalCells = (
  worksheet: ExcelJS.Worksheet,
  HEADERS: string[] | VerticalSeasons,
  column: string,
  row: number,
  mergeNum: number,
) => {
  if (!HEADERS.length) {
    return;
  }
  const [head, ...tail] = HEADERS;
  // ignore cells with 1
  const cell = `${column}${row}`;
  if (typeof head === "string") {
    if (mergeNum > 0) {
      const mergeValue = `${cell}:${column}${row + mergeNum}`;
      worksheet.mergeCells(mergeValue);
    }
    const worksheetCell = worksheet.getCell(cell);
    styleCell(worksheetCell, head);
  } else {
    const mergeValue = `${cell}:${column}${row + head.rowSpan - 1}`;
    worksheet.mergeCells(mergeValue);
    const worksheetCell = worksheet.getCell(cell);
    styleCell(worksheetCell, head.value);
  }
  // set accent style to match headers
  const merge = typeof head === "string" ? mergeNum + 1 : head.rowSpan;
  return mergeHorizontalCells(
    worksheet,
    tail as string[] | VerticalSeasons,
    column,
    row + merge,
    mergeNum,
  );
};

const addTab = (worksheet: ExcelJS.Worksheet, tab: string, row: number) => {
  const cell = `${CELL_VALUES[0]}${row}`;
  const mergeValue = `${cell}:${CELL_VALUES[1]}${row}`;
  worksheet.mergeCells(mergeValue);
  const cellToStyle = worksheet.getCell(cell);
  styleCell(cellToStyle, tab);
  cellToStyle.font = {
    size: 16,
  };
};

type ShipmentSubmitTables = {
  atOnce: Array<Array<number>>;
  distribution: Array<Array<number | string>>;
  futures: Array<Array<number>>;
  futuresTotals: number[];
  seasonToMonthData: number[][];
  cupData: number[][];
};

const baseTableColumns = (horizontalHeaders: HorizontalHeaders) =>
  horizontalHeaders.MONTHS.map((header) => ({
    name: header,
    totalsRowLabel: header,
  })).concat({ name: "Total", totalsRowLabel: "Total" });

const addTable = (
  worksheet: ExcelJS.Worksheet,
  name: string,
  columns: any[],
  rows: Array<Array<number | string>>,
  tableColumns: string[],
  refLength: number,
  horizontalHeaders: HorizontalHeaders,
  headerRow = true,
) => {
  worksheet.addTable({
    name,
    ref: `${CELL_VALUES[2]}${refLength}`,
    headerRow,
    style: {
      theme: "TableStyleMedium22",
    },
    columns,
    rows,
  });
  const length = headerRow ? refLength + 1 : refLength;
  mergeVerticalByRow(worksheet, tableColumns, length);
  new Array(rows.length).fill(0).forEach((_, i) => {
    const row = length + i;
    CELL_VALUES.slice(2, 3 + horizontalHeaders.MONTHS.length).forEach(
      (cellVal) => {
        const cell = `${cellVal}${row}`;
        worksheet.getCell(cell).fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "FFFFFF" },
        };
      },
    );
  });
};

const setLabel = (
  worksheet: ExcelJS.Worksheet,
  label: string,
  row: number,
  mergeVal: number,
) => {
  const mergeValue = `${CELL_VALUES[0]}${row}:${CELL_VALUES[mergeVal]}${row}`;
  worksheet.mergeCells(mergeValue);
  const cell = worksheet.getCell(`${CELL_VALUES[0]}${row}`);
  cell.value = label;
  cell.font = {
    size: 22,
  };
};
export const createWorksheet = async (
  tab: string,
  arrays: ShipmentSubmitTables,
  isNVS: boolean,
  headers: {
    horizontalHeaders: HorizontalHeaders;
    verticalSeasons: VerticalSeasons;
    verticalMonths: string[];
    monthsInFirstTwoSeasons: number;
  },
  is3PW: boolean,
) => {
  const { horizontalHeaders, verticalSeasons, verticalMonths } = headers;
  const columns = baseTableColumns(horizontalHeaders);
  const workbook = new ExcelJS.Workbook();
  workbook.description = `${tab} Forecast`;
  const worksheet = workbook.addWorksheet(tab.toUpperCase());

  const TOP_OF_PAGE_ADJUSTMENT = TABLES_START + TABLE_SEPARATION;
  mergeCells(worksheet, horizontalHeaders.SEASONS, TABLES_START, 2);
  mergeCells(worksheet, horizontalHeaders.QUARTERS, TABLES_START + 1, 2);

  if (is3PW) {
    mergeHorizontalCells(
      worksheet,
      verticalSeasons,
      CELL_VALUES[0],
      TABLES_START + 3,
      2,
    );
    mergeHorizontalCells(
      worksheet,
      verticalMonths,
      CELL_VALUES[1],
      TABLES_START + 3,
      0,
    );
  } else {
    mergeVerticalByRow(worksheet, ["Futures Total"], TABLES_START + 3);
  }
  addTab(worksheet, tab, TABLE_SEPARATION);
  const futuresRow: Array<Array<string | number>> = arrays["futures"];
  const horizontalHeaderColumns = horizontalHeaders.MONTHS.map((month) => ({
    name: month,
    totalsRowLabel: month,
  }));
  const futuresColumns = is3PW
    ? horizontalHeaderColumns.concat([
        { name: "Total", totalsRowLabel: "Total" },
        { name: "PG2N Units", totalsRowLabel: "PG2N Units" },
        { name: "Var", totalsRowLabel: "Var" },
      ])
    : horizontalHeaderColumns.concat([
        { name: "Total Units", totalsRowLabel: "Total Units" },
      ]);

  worksheet.addTable({
    name: "Futures",
    ref: `${CELL_VALUES[2]}${TABLES_START + 2}`,
    style: {
      theme: "TableStyleMedium22",
    },
    headerRow: true,
    columns: futuresColumns,
    rows: futuresRow,
  });
  setLabel(worksheet, "Futures", TABLES_START - 1, 1);
  const futuresTotals = [...arrays["futuresTotals"]];
  const totalFutures = futuresTotals.concat(
    futuresTotals.reduce((acc, curr) => acc + curr, 0),
  );
  if (is3PW) {
    const pg2nAndVar = futuresRow.reduce(
      (acc: number[], row): number[] => {
        const pg2nUnits = row[row.length - 2];
        const varianceVal = row[row.length - 1];
        const units =
          typeof pg2nUnits === "number" ? pg2nUnits : parseInt(pg2nUnits);
        const variance =
          typeof varianceVal === "number" ? varianceVal : parseInt(varianceVal);
        return [acc[0] + units, acc[1] + variance];
      },
      [0, 0],
    );
    const totals = totalFutures.concat(pg2nAndVar as number[]);
    const table = worksheet.getTable("Futures");
    table.addRow(totals);
    const cupData: Array<Array<number>> = arrays["cupData"];
    const lrTotals = cupData[cupData.length - 1];
    const finalCupData = Array(headers.monthsInFirstTwoSeasons)
      .fill("-")
      .concat(lrTotals.slice(0, lrTotals.length - 3))
      .concat([
        cupData[cupData.length - 1].reduce((acc, curr) => acc + curr, 0),
      ])
      .concat(Array(2).fill("-"));
    table.addRow(finalCupData);
    const varianceRow = totals
      .slice(0, totals.length - 3)
      .slice(headers.monthsInFirstTwoSeasons)
      .map((val, i) => Math.abs(val - lrTotals[i]));
    table.addRow(
      Array(headers.monthsInFirstTwoSeasons)
        .fill("-")
        .concat(varianceRow)
        .concat(Array(3).fill("")),
    );
    table.commit();
    const rowLength = TABLES_START + futuresRow.length;
    mergeVerticalByRow(
      worksheet,
      ["Total Units", "LR Totals", "Var"],
      rowLength,
    );
  }
  new Array(futuresRow.length).fill(0).forEach((_, i) => {
    const row = TABLE_SEPARATION + 1 + i;
    CELL_VALUES.slice(2, 2 + horizontalHeaders.MONTHS.length + 3).forEach(
      (cellVal) => {
        const cell = `${cellVal}${row}`;
        worksheet.getCell(cell).fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "FFFFFF" },
        };
      },
    );
  });
  const FUTURE_TABLE_TOTAL_ROWS_COUNT = is3PW
    ? futuresRow.length + 3
    : futuresRow.length;

  const distributionRows = arrays["distribution"];
  const DISTRIBUTION_START = TOP_OF_PAGE_ADJUSTMENT + futuresRow.length + 3;
  addTable(
    worksheet,
    "distribution",
    columns,
    distributionRows,
    DISTRIBUTION_COLUMNS,
    DISTRIBUTION_START,
    horizontalHeaders,
  );
  setLabel(worksheet, "Distribution Method", DISTRIBUTION_START - 3, 3);
  addTab(worksheet, tab, DISTRIBUTION_START);

  const atOnceData = arrays["atOnce"];
  const AT_ONCE_START =
    DISTRIBUTION_START + distributionRows.length + TABLE_SEPARATION + 1;
  addTable(
    worksheet,
    "AtOnce",
    columns,
    atOnceData,
    isNVS ? AT_ONCE_COLUMNS : AT_ONCE_COLUMNS.slice(0, 1),
    AT_ONCE_START,
    horizontalHeaders,
  );
  setLabel(worksheet, "At Once", AT_ONCE_START - 3, 1);
  addTab(worksheet, tab, AT_ONCE_START);

  const atOnceTotals: number[] = [...atOnceData[atOnceData.length - 1]];
  const totals = [totalFutures].concat([atOnceTotals]).reduce((acc, curr) => {
    if (!acc.length) {
      return curr;
    }
    return acc.map((val, i) => val + curr[i]);
  }, []);
  const TOTALS_TABLE_START =
    AT_ONCE_START + atOnceData.length + TABLE_SEPARATION + 1;
  addTable(
    worksheet,
    `Total${tab}`,
    columns,
    [totalFutures, atOnceTotals, totals],
    TOTAL_BUSINESS_MODEL_HEADERS,
    TOTALS_TABLE_START,
    horizontalHeaders,
  );
  setLabel(
    worksheet,
    `Total ${tab} (Futures + At Once)`,
    TOTALS_TABLE_START - 3,
    5,
  );
  addTab(worksheet, tab, TOTALS_TABLE_START);

  const buffer = await workbook.xlsx.writeBuffer();
  const fileType =
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8";
  // @ts-ignore
  return window.URL.createObjectURL(new Blob([buffer], { type: fileType }));
};
